import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"

import { Globe } from "@jobteaser/spark/components/Icons/Globe"

import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./DropdownContent.module.css"

type BaseProps = {
  hasError: boolean
  isLoading: boolean
  suggestedOptions: SimpleSelectOptions[]
  onOptionClick: (option: SimpleSelectOptions) => void
  currentInput: string
  selectedLocation: SimpleSelectOptions | undefined
}

type AbroadEnabledProps = BaseProps & {
  enableAbroadOption: boolean
  onAbroadOptionClick: () => void
}

type AbroadDisabledProps = BaseProps & {
  enableAbroadOption?: never
  onAbroadOptionClick?: never
}

export type DropdownContentProps = AbroadEnabledProps | AbroadDisabledProps

export const DropdownContent: FunctionComponent<DropdownContentProps> = ({
  hasError,
  isLoading,
  suggestedOptions,
  onOptionClick,
  currentInput,
  selectedLocation,
  enableAbroadOption,
  onAbroadOptionClick,
}) => {
  const { t } = useTranslation(["job_ads_fo"])

  if (hasError) {
    return (
      <div className={clsx(styles.main__dropdownContent, styles.main__bodyText, styles.main__error)}>
        {t("job_ads_fo.client.search.algolia_location_filter.content.error")}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={clsx(styles.main__dropdownContent, styles.main__bodyText)}>
        {t("job_ads_fo.client.search.algolia_location_filter.content.loading")}
      </div>
    )
  }

  if (suggestedOptions.length !== 0) {
    return (
      <>
        {suggestedOptions.map(option => (
          <button
            type="button"
            key={option.value}
            onClick={() => onOptionClick(option)}
            className={clsx(styles.main__dropdownItem, styles.main__bodyText)}
          >
            {option.label}
          </button>
        ))}
      </>
    )
  }

  /**
   * OPTIMIZE: could use a boolean as props that would replace `currentInput !== "" && currentInput !== selectedLocation?.label`
   */
  if (suggestedOptions.length === 0 && currentInput !== "" && currentInput !== selectedLocation?.label) {
    return (
      <div className={clsx(styles.main__dropdownContent, styles.main__bodyText)}>
        {t("job_ads_fo.client.search.algolia_location_filter.content.no_result")}
      </div>
    )
  }

  if (enableAbroadOption) {
    return (
      <button
        type="button"
        onClick={onAbroadOptionClick}
        className={clsx(styles.main__dropdownItem, styles.main__bodyText, styles.main__dropdownAbroadOption)}
      >
        <Globe className={styles.main__abroadIcon} />
        {t("job_ads_fo.client.search.algolia_location_filter.abroad_only")}
      </button>
    )
  }

  return (
    <div className={clsx(styles.main__dropdownContent, styles.main__bodyText)}>
      {t("job_ads_fo.client.search.algolia_location_filter.content.idle")}
    </div>
  )
}
